import * as React from "react"
import { ChevronLeft, ChevronRight, DownloadIcon, ExternalLink, Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"
import { cn } from "../../../lib/utils"
import { FACEBOOK_FEED_ASPECT_CLASS, type MediaSlide } from "./media-preview"

export interface MediaPreviewPanelProps {
  src?: string
  thumbnailSrc: string
  alt?: string
  slides?: MediaSlide[]
  type: "video" | "image"
  /** External link (e.g. Ad Library URL) */
  externalUrl?: string
  /** Resets slide index and loading when the creative changes */
  contentKey?: string
  onMediaReady?: () => void
  /** Fired when the visible carousel slide changes (including reset to 0 on contentKey change). */
  onSlideChange?: (index: number) => void
  className?: string
}

export interface MediaPreviewPanelHandle {
  seekTo: (seconds: number) => void
}

function BrokenImageIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className={className}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3 3l18 18M9 9.75A2.25 2.25 0 1111.25 12 2.25 2.25 0 019 9.75z"
      />
    </svg>
  )
}

export const MediaPreviewPanel = React.forwardRef<MediaPreviewPanelHandle, MediaPreviewPanelProps>(
  function MediaPreviewPanel(
    {
      src,
      thumbnailSrc,
      type,
      alt = "",
      slides,
      externalUrl,
      contentKey,
      onMediaReady,
      onSlideChange,
      className,
    },
    ref,
  ) {
  const { t } = useTranslation()
  const [currentSlideIndex, setCurrentSlideIndex] = React.useState(0)
  const [mediaLoading, setMediaLoading] = React.useState(true)
  const [mediaError, setMediaError] = React.useState(false)
  const [mediaFallback, setMediaFallback] = React.useState(false)
  const [downloading, setDownloading] = React.useState(false)
  const videoRef = React.useRef<HTMLVideoElement>(null)

  React.useImperativeHandle(
    ref,
    () => ({
      seekTo(seconds: number) {
        const video = videoRef.current
        if (!video) return
        video.currentTime = seconds
        void video.play().catch(() => {})
      },
    }),
    [],
  )

  const isCarousel = Boolean(slides?.length)
  const activeSlide = slides?.[currentSlideIndex]
  const fallbackType: "image" | "video" = activeSlide?.type === "image" ? "video" : "image"
  const activeType = mediaFallback ? fallbackType : (activeSlide?.type ?? type)
  const activeThumbnail = activeSlide?.thumbnailUrl ?? thumbnailSrc
  const activeSrc = isCarousel ? activeSlide?.assetUrl : src

  const effectiveSrc = mediaFallback && activeSrc
    ? activeSrc.replace(
        activeSlide?.type === "image" ? /\.png$/ : /\.mp4$/,
        activeSlide?.type === "image" ? ".mp4" : ".png",
      )
    : activeSrc

  const markReady = React.useCallback(() => {
    setMediaLoading(false)
    onMediaReady?.()
  }, [onMediaReady])

  const goToSlide = React.useCallback(
    (index: number) => {
      if (!slides) return
      videoRef.current?.pause()
      setCurrentSlideIndex(index)
      setMediaLoading(true)
      setMediaError(false)
      setMediaFallback(false)
      onSlideChange?.(index)
    },
    [slides, onSlideChange],
  )

  React.useEffect(() => {
    setCurrentSlideIndex(0)
    setMediaLoading(true)
    setMediaError(false)
    setMediaFallback(false)
    onSlideChange?.(0)
  }, [contentKey, onSlideChange])

  React.useEffect(() => {
    if (!isCarousel || !slides) return
    function handleKey(e: KeyboardEvent) {
      if (e.key === "ArrowLeft" && currentSlideIndex > 0) goToSlide(currentSlideIndex - 1)
      if (e.key === "ArrowRight" && slides && currentSlideIndex < slides.length - 1) goToSlide(currentSlideIndex + 1)
    }
    document.addEventListener("keydown", handleKey)
    return () => document.removeEventListener("keydown", handleKey)
  }, [isCarousel, slides, currentSlideIndex, goToSlide])

  const handleDownload = React.useCallback(async () => {
    if (!effectiveSrc || downloading) return
    setDownloading(true)
    try {
      const res = await fetch(effectiveSrc)
      if (!res.ok) throw new Error(`${res.status}`)
      const blob = await res.blob()
      const blobUrl = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = blobUrl
      a.download = effectiveSrc.split("/").pop()?.split("?")[0] ?? "download"
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(blobUrl)
    } catch {
      window.open(effectiveSrc, "_blank", "noopener,noreferrer")
    } finally {
      setDownloading(false)
    }
  }, [effectiveSrc, downloading])

  return (
    <div className={cn("flex flex-col", className)}>
      <div className="relative flex flex-1 items-center justify-center px-12 py-8">
        {isCarousel && slides && slides.length > 1 ? (
          <>
            <button
              type="button"
              onClick={() => goToSlide(currentSlideIndex - 1)}
              disabled={currentSlideIndex === 0}
              className="absolute left-3 top-1/2 z-10 flex size-8 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm transition-colors hover:text-foreground disabled:opacity-30"
              aria-label={t("common.mediaPreview.previousSlide", "Previous slide")}
            >
              <ChevronLeft className="size-4" />
            </button>
            <button
              type="button"
              onClick={() => goToSlide(currentSlideIndex + 1)}
              disabled={currentSlideIndex === slides.length - 1}
              className="absolute right-3 top-1/2 z-10 flex size-8 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm transition-colors hover:text-foreground disabled:opacity-30"
              aria-label={t("common.mediaPreview.nextSlide", "Next slide")}
            >
              <ChevronRight className="size-4" />
            </button>
          </>
        ) : null}

        <div className="relative w-full max-w-[320px] overflow-hidden rounded-[14px] bg-muted shadow-md">
          {mediaError ? (
            <div className={cn("flex flex-col items-center justify-center gap-2 bg-muted", FACEBOOK_FEED_ASPECT_CLASS)}>
              <BrokenImageIcon className="size-10 text-muted-foreground/40" />
              <p className="text-xs text-muted-foreground">{t("common.mediaPreview.failedToLoad")}</p>
            </div>
          ) : activeType === "video" && effectiveSrc ? (
            <video
              ref={videoRef}
              key={effectiveSrc}
              src={effectiveSrc}
              poster={activeThumbnail}
              controls
              playsInline
              className={cn(FACEBOOK_FEED_ASPECT_CLASS, "w-full bg-black object-contain")}
              onCanPlay={markReady}
              onError={() => {
                if (isCarousel && activeSlide?.type === "video" && !mediaFallback) {
                  setMediaFallback(true)
                  setMediaLoading(true)
                } else {
                  setMediaLoading(false)
                  setMediaError(true)
                }
              }}
            />
          ) : effectiveSrc ? (
            <img
              src={effectiveSrc}
              alt={alt}
              className={cn(
                FACEBOOK_FEED_ASPECT_CLASS,
                "w-full object-contain",
                mediaLoading ? "opacity-0" : "opacity-100 transition-opacity duration-300",
              )}
              onLoad={markReady}
              onError={() => {
                if (isCarousel && activeSlide?.type === "image" && !mediaFallback) {
                  setMediaFallback(true)
                  setMediaLoading(true)
                } else if (effectiveSrc !== thumbnailSrc && thumbnailSrc) {
                  setMediaLoading(true)
                } else {
                  setMediaLoading(false)
                  setMediaError(true)
                }
              }}
            />
          ) : (
            <img src={thumbnailSrc} alt={alt} className={cn(FACEBOOK_FEED_ASPECT_CLASS, "w-full object-contain")} onLoad={markReady} />
          )}

          {mediaLoading && !mediaError ? (
            <div className="absolute inset-0 flex items-center justify-center bg-muted/80">
              <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
          ) : null}
        </div>
      </div>

      {isCarousel && slides && slides.length > 1 ? (
        <div className="flex justify-center gap-1.5 overflow-x-auto px-5 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {slides.map((s, i) => (
            <button
              key={s.assetUrl}
              type="button"
              onClick={() => goToSlide(i)}
              className={cn(
                "size-8 shrink-0 overflow-hidden rounded transition-all",
                i === currentSlideIndex
                  ? "border-2 border-primary opacity-100"
                  : "border-2 border-transparent opacity-50 hover:opacity-80",
              )}
              aria-label={t("common.mediaPreview.goToSlide", "Go to slide {{n}}", { n: i + 1 })}
              aria-current={i === currentSlideIndex ? "true" : undefined}
            >
              <img src={s.thumbnailUrl} alt="" className="size-full object-cover" />
            </button>
          ))}
        </div>
      ) : null}

      <div className="flex items-center gap-2 border-t border-border px-5 py-4">
        {effectiveSrc ? (
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            className="flex size-8 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
            aria-label={t("common.mediaPreview.download")}
          >
            {downloading ? <Loader2 className="size-3.5 animate-spin" /> : <DownloadIcon className="size-3.5" />}
          </button>
        ) : null}
        {externalUrl && externalUrl !== "#" ? (
          <a
            href={externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex size-8 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground transition-colors hover:text-foreground"
            aria-label={t("common.openExternal")}
          >
            <ExternalLink className="size-3.5" />
          </a>
        ) : null}
        {isCarousel && slides ? (
          <span className="ml-auto tabular-nums text-[11px] text-muted-foreground">
            {currentSlideIndex + 1} / {slides.length}
          </span>
        ) : null}
      </div>
    </div>
  )
},
)
